import qs from 'qs'
import { toString, mapValues, isEmpty } from 'lodash'
import { defineStore } from 'pinia'
import TabStorage from '@/utils/tab_storage.js'
import {
  DEFAULT_SORT_COLUMN,
  DEFAULT_SORT_DIRECTION,
  sortStringForSearch
} from '@/store/shared/sorting.js'

const TAB_STORAGE_KEY = 'last-story-index-search'
export const DEFAULT_SEARCH = {
  text_cont: null,
  user_id_in: [],
  story_sections_section_id_in: [],
  story_publications_publication_id_in: [],
  subsite_ids_in: [],
  squeue_id_in: [],
  slug_cont: null,
  byline_cont: null,
  content_cont: null,
  pub_date_gteq: null,
  pub_date_lteq: null,
  pub_date_within: null,
  custom_folders_path_eq: null,
  web_category_id_eq: null,
  sort_column: DEFAULT_SORT_COLUMN,
  sort_direction: DEFAULT_SORT_DIRECTION
}

export const useStorySearchStore = defineStore('storySearch', {
  state: () => ({
    search: Object.assign({}, DEFAULT_SEARCH)
  }),

  getters: {
    sort({ search }) {
      const col = search.sort_column || DEFAULT_SORT_COLUMN
      const dir = search.sort_direction || DEFAULT_SORT_DIRECTION
      return `${col} ${dir}`
    },

    relevanceSortAvailable({ search }) {
      return !isEmpty(search?.text_cont)
    },

    anyAdvancedQueries({ search }) {
      return !!(
        search.slug_cont ||
        search.byline_cont ||
        search.content_cont ||
        search.pub_date_gteq ||
        search.pub_date_lteq ||
        search.pub_date_within ||
        search.custom_folders_path_eq ||
        search.web_category_id_eq
      )
    },

    anyQueries({ search }) {
      if (this.anyAdvancedQueries) return true

      return !!(
        search.text_cont ||
        search.user_id_in.length > 0 ||
        search.story_sections_section_id_in.length > 0 ||
        search.story_publications_publication_id_in.length > 0 ||
        search.squeue_id_in.length > 0 ||
        search.sort_column !== DEFAULT_SEARCH.sort_column ||
        search.sort_direction !== DEFAULT_SEARCH.sort_direction
      )
    },

    normalized({ search }) {
      return mapValues(search, val => val ? val : null)
    },

    withDefaultsExcluded() {
      return Object.keys(this.normalized).reduce((diff, key) => {
        if (this.normalized[key] === DEFAULT_SEARCH[key]) return diff
        return { ...diff, [key]: this.normalized[key] }
      }, {})
    },

    forQueryParams() {
      const { sort_column, sort_direction, ...withoutSort } = this.withDefaultsExcluded
      const sortVal = sortStringForSearch(this.normalized)
      return sortVal ? { ...withoutSort, s: sortVal } : withoutSort
    }
  },

  actions: {
    saveToWindowStore() {
      TabStorage.set(TAB_STORAGE_KEY, this.search || {})
    },

    loadFromWindowStore() {
      const storedSearch = TabStorage.fetch(TAB_STORAGE_KEY)
      if (storedSearch) this.search = storedSearch
    },

    parse() {
      const parsed = qs.parse(
        window.location.search,
        { ignoreQueryPrefix: true, skipNulls: true }
      )

      const query = (parsed['q'] || {})
      const user_id_in = (query.user_id_in || []).map(Number)
      const story_sections_section_id_in = (query.story_sections_section_id_in || []).map(Number)
      const story_publications_publication_id_in = (query.story_publications_publication_id_in || []).map(Number)
      const subsite_ids_in = (query.subsite_ids_in || []).map(Number)
      const squeue_id_in = (query.squeue_id_in || []).map(Number)
      const web_category_id_eq = query.web_category_id_eq ? Number(query.web_category_id_eq) : null

      const { s, ...withoutSort } = query
      const [sort_column, sort_direction] = toString(s).split(' ')
      const sort = sort_column && sort_direction ? { sort_column, sort_direction } : {}

      this.search = Object.assign(
        {},
        DEFAULT_SEARCH,
        withoutSort,
        { user_id_in, story_sections_section_id_in, story_publications_publication_id_in, subsite_ids_in, squeue_id_in, web_category_id_eq },
        sort
      )
    },

    reset() {
      this.search = Object.assign({}, DEFAULT_SEARCH)
      TabStorage.set(TAB_STORAGE_KEY, null)
    },

    parseOrLoad() {
      const parsed = qs.parse(
        window.location.search,
        { ignoreQueryPrefix: true, skipNulls: true }
      )

      if (parsed['q']) {
        this.parse()
      } else {
        this.loadFromWindowStore()
      }
    }
  }
})
